// ISDP CUSTOM: Entire file — Dräger corporate theme with ODX design system overrides
// Primary: #002766 (Dräger navy), Accent: #0091f7 (cyan-500)
// All MUI component overrides set here so they apply at the theme level,
// avoiding specificity battles with external CSS.
//
// Light and dark are generated from the same component factory (makeComponents)
// fed two palette objects, so the two themes can never structurally drift — only
// their ~20 palette values differ. The CSS-level light-dark() approach continues
// to work alongside these MUI themes; ThemeProviderWrapper in App.tsx keeps the
// CSS classes and colorScheme in sync.
import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
  type UnifiedThemeOptions,
} from '@backstage/theme';

const odxFont =
  '"Draeger Pangea Text", "DraegerPangeaText", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const navy = '#002766';
const cyan = '#0091f7';
const radius = 6;
const radiusControls = 3;

/**
 * The full set of values that differ between the light and dark themes.
 * Everything else (structure, fonts, radii, the cyan accent) is shared.
 */
type ThemePalette = {
  base: UnifiedThemeOptions['palette'];
  primaryMain: string; // contained-primary button + selected pagination
  bgDefault: string;
  bgPaper: string;
  textPrimary: string;
  textSecondary: string;
  separator: string;
  navBackground: string;
  navHover: string;
  pageColor: string; // genPageTheme header band
  buttonHover: string;
  tableHeadBorder: string;
  tableBodyBorder: string;
  tableRowHover: string;
  chipBg: string;
  inputHoverBorder: string;
  iconButtonHover: string;
  menuItemSelected: string;
  progressBg: string;
  stepCompleted: string;
  linkHover: string;
  elevation8Shadow: string;
};

const lightPalette: ThemePalette = {
  base: palettes.light,
  primaryMain: navy,
  bgDefault: '#f5f7fa',
  bgPaper: '#ffffff',
  textPrimary: navy,
  textSecondary: '#798ea0',
  separator: '#d9e1e8',
  navBackground: '#003369',
  navHover: '#004A8F',
  pageColor: navy,
  buttonHover: '#003d99',
  tableHeadBorder: '#5d6a74', // --gray-900: strong header separator
  tableBodyBorder: '#dde5ee', // --gray-200: subtle row separator
  tableRowHover: 'rgba(0, 39, 102, 0.05)', // --blue-700-5
  chipBg: '#e9eef4', // --gray-100: ODX secondary chip bg
  inputHoverBorder: '#b0bec5',
  iconButtonHover: 'rgba(0, 39, 102, 0.05)',
  menuItemSelected: 'rgba(0, 145, 247, 0.08)',
  progressBg: '#e8edf2',
  stepCompleted: '#00823c',
  linkHover: '#0077cc',
  elevation8Shadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
};

const darkPalette: ThemePalette = {
  base: palettes.dark,
  primaryMain: cyan,
  bgDefault: '#0d1b2a',
  bgPaper: '#1a2332',
  textPrimary: '#e0e8f0',
  textSecondary: '#8ca0b3',
  separator: '#2a3a4a',
  navBackground: '#0a1628',
  navHover: '#112240',
  pageColor: '#0a1628',
  buttonHover: '#0077cc',
  tableHeadBorder: '#3a4a5a',
  tableBodyBorder: '#2a3a4a',
  tableRowHover: 'rgba(0, 145, 247, 0.08)',
  chipBg: '#1e2d3d',
  inputHoverBorder: '#4a5a6a',
  iconButtonHover: 'rgba(0, 145, 247, 0.1)',
  menuItemSelected: 'rgba(0, 145, 247, 0.15)',
  progressBg: '#1e2d3d',
  stepCompleted: '#00a84a',
  linkHover: '#3ab0ff',
  elevation8Shadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
};

const makePalette = (p: ThemePalette): UnifiedThemeOptions['palette'] => ({
  ...p.base,
  primary: { main: p.primaryMain },
  secondary: { main: cyan },
  background: { default: p.bgDefault, paper: p.bgPaper },
  text: { primary: p.textPrimary, secondary: p.textSecondary },
  divider: p.separator,
  navigation: {
    background: p.navBackground,
    indicator: cyan,
    color: '#ffffff',
    selectedColor: '#ffffff',
    navItem: { hoverBackground: p.navHover },
  },
});

const makePageTheme = (p: ThemePalette): UnifiedThemeOptions['pageTheme'] => ({
  home: genPageTheme({ colors: [p.pageColor, p.pageColor], shape: shapes.wave }),
  documentation: genPageTheme({ colors: [p.pageColor, p.pageColor], shape: shapes.wave2 }),
  tool: genPageTheme({ colors: [p.pageColor, p.pageColor], shape: shapes.round }),
  other: genPageTheme({ colors: [p.pageColor, p.pageColor], shape: shapes.wave }),
});

/** MUI v5 component overrides (Backstage unified theme merges v4 + v5). */
const makeComponents = (p: ThemePalette): UnifiedThemeOptions['components'] => ({
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: radius, boxShadow: 'none', border: `1px solid ${p.separator}` },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: radius },
      elevation1: { boxShadow: 'none', border: `1px solid ${p.separator}` },
      elevation2: { boxShadow: 'none', border: `1px solid ${p.separator}` },
      elevation3: { boxShadow: 'none', border: `1px solid ${p.separator}` },
      elevation4: { boxShadow: 'none', border: `1px solid ${p.separator}` },
      elevation8: { boxShadow: p.elevation8Shadow, border: `1px solid ${p.separator}` },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: radiusControls,
        fontFamily: odxFont,
        textTransform: 'none' as const,
        fontWeight: 500,
      },
      containedPrimary: {
        backgroundColor: p.primaryMain,
        color: '#ffffff',
        '&:hover': { backgroundColor: p.buttonHover },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: odxFont,
        textTransform: 'none' as const,
        fontWeight: 500,
        fontSize: '14px',
        minWidth: 'auto',
        padding: '12px 20px',
      },
    },
  },
  MuiTabs: { styleOverrides: { indicator: { backgroundColor: cyan } } },
  MuiTable: {
    styleOverrides: {
      root: {
        background: 'transparent',
        borderSpacing: 0,
        minWidth: '100%',
        tableLayout: 'auto' as const,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontFamily: odxFont,
        fontWeight: 500,
        fontSize: '14px',
        letterSpacing: '0.01em',
        color: p.textPrimary,
        backgroundColor: 'transparent',
        borderBottomColor: p.tableHeadBorder,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid' as const,
        padding: '12px',
        textTransform: 'none' as const,
      },
      body: {
        fontFamily: odxFont,
        fontSize: '14px',
        color: p.textPrimary,
        borderBottomColor: p.tableBodyBorder,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid' as const,
        padding: '12px',
        verticalAlign: 'middle' as const,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: 'background-color 0.15s',
        '&:hover': { backgroundColor: p.tableRowHover },
      },
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        fontFamily: odxFont,
        fontWeight: 500,
        color: `${p.textPrimary} !important`,
        '&.Mui-active': { color: `${p.textPrimary} !important` },
      },
      icon: { color: `${p.textSecondary} !important` },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        fontFamily: odxFont,
        fontWeight: 500,
        fontSize: '13px',
        height: 'auto',
        padding: '0 4px',
        backgroundColor: p.chipBg,
        color: p.textPrimary,
        border: 'none',
      },
      sizeSmall: { fontSize: '12px', height: 'auto', padding: '0 2px' },
      label: { padding: '4px 8px' },
      labelSmall: { padding: '2px 6px' },
      deleteIcon: {
        color: p.textSecondary,
        '&:hover': { color: p.textPrimary },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radiusControls,
        fontFamily: odxFont,
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: p.inputHoverBorder },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: cyan, borderWidth: '1px' },
      },
      notchedOutline: { borderColor: p.separator },
    },
  },
  MuiInputLabel: { styleOverrides: { root: { fontFamily: odxFont } } },
  MuiDialog: { styleOverrides: { paper: { borderRadius: radius } } },
  MuiAccordion: {
    styleOverrides: {
      root: {
        border: `1px solid ${p.separator}`,
        boxShadow: 'none',
        borderRadius: `${radius}px`,
        '&::before': { display: 'none' },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: { tooltip: { fontFamily: odxFont, borderRadius: radiusControls } },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { color: p.textSecondary, '&:hover': { backgroundColor: p.iconButtonHover } },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontFamily: odxFont,
        fontSize: '14px',
        '&.Mui-selected': { backgroundColor: p.menuItemSelected },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: 2, backgroundColor: p.progressBg },
      bar: { backgroundColor: cyan },
    },
  },
  MuiAlert: { styleOverrides: { root: { borderRadius: radius, fontFamily: odxFont } } },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        borderRadius: radiusControls,
        fontFamily: odxFont,
        '&.Mui-selected': { backgroundColor: p.primaryMain, color: '#fff' },
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active': { color: cyan },
        '&.Mui-completed': { color: p.stepCompleted },
      },
    },
  },
  MuiStepLabel: { styleOverrides: { label: { fontFamily: odxFont } } },
  MuiBreadcrumbs: { styleOverrides: { li: { fontFamily: odxFont } } },
  MuiLink: {
    styleOverrides: { root: { color: cyan, '&:hover': { color: p.linkHover } } },
  },
});

const makeTheme = (p: ThemePalette) =>
  createUnifiedTheme({
    palette: makePalette(p),
    fontFamily: odxFont,
    defaultPageTheme: 'home',
    pageTheme: makePageTheme(p),
    components: makeComponents(p),
  });

export const draegerLightTheme = makeTheme(lightPalette);
export const draegerDarkTheme = makeTheme(darkPalette);
